/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSonnerToast } from '@/shared/ui';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { agendaKeys } from './agenda-keys';

const GOOGLE_CONNECT_PARAM = 'google_connect';

/**
 * Strip the Google connect params from the URL without a navigation.
 *
 * Removes `google_connect` and `calendar_id` so a refresh won't re-toast.
 */
const clearConnectParams = (): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(GOOGLE_CONNECT_PARAM);
  url.searchParams.delete('calendar_id');
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
};

/**
 * Handle the browser return from the Google OAuth callback.
 *
 * On mount, reads `?google_connect=success|failure`. Success toasts, revalidates
 * the agency calendars (so the connected badge appears) and clears the params;
 * failure toasts an error and clears the params. No params is a no-op.
 *
 * @param agencyId The agency whose calendars should be revalidated.
 */
export const useGoogleConnectReturn = (agencyId: string): void => {
  const { t } = useTranslation('features/agenda');
  const { showSuccess } = useSonnerToast();
  const queryClient = useQueryClient();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    const status = new URLSearchParams(window.location.search).get(
      GOOGLE_CONNECT_PARAM
    );
    if (!status) return;
    handled.current = true;

    if (status === 'success') {
      showSuccess(t('toast.google_connected'));
      queryClient.invalidateQueries({
        queryKey: agendaKeys.calendars(agencyId),
      });
    } else {
      reportError(undefined, { message: t('toast.google_connect_failed') });
    }
    clearConnectParams();
  }, [agencyId, queryClient, showSuccess, t]);
};
